import React, { useCallback, useEffect, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDashboardDialog } from '@actions/dashboard-actions';
import { setDateRange } from '@actions/groups-actions';
import * as dialog from '@constants/dialogs';
import BaseDateRangeDialog, { useDateRangeDialog } from '@shared/dialogs/base-date-range-dialog';
import { dateToString } from '@utils/date-utils';

/**
 * Group date range dialog component.
 * @returns {JSX.Element|null} The rendered dialog or null.
 */
const DateRange = () => {
  const dispatch = useDispatch();

  const activeDialogs = useSelector(state => state.dashboard.activeDialogs);
  const autoArea = useSelector(state => state.groups.edit.group.auto_area);
  const { start_date: startProp, end_date: endProp } = autoArea || { start_date: null, end_date: null };

  const {
    start,
    end,
    resetToDefault,
    handleStartChange,
    handleEndChange
  } = useDateRangeDialog({
    start: startProp,
    end: endProp,
    contain: false,
    useGlobalDefaults: false
  });

  // Sync state when dialog opens
  const prevOpenRef = useRef(activeDialogs[dialog.DATE_RANGE]);
  useEffect(() => {
    const isOpen = activeDialogs[dialog.DATE_RANGE];
    const wasOpen = prevOpenRef.current;
    if (!wasOpen && isOpen) {
      resetToDefault();
    }
    prevOpenRef.current = isOpen;
  }, [activeDialogs, resetToDefault]);

  const getFilters = useCallback(() => ({
    start_date: dateToString(start),
    end_date: dateToString(end)
  }), [start, end]);

  const closeDialog = useCallback(() => {
    dispatch(closeDashboardDialog(dialog.DATE_RANGE));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    resetToDefault();
    closeDialog();
  }, [resetToDefault, closeDialog]);

  const handleApply = useCallback(() => {
    dispatch(setDateRange(getFilters()));
    closeDialog();
  }, [dispatch, getFilters, closeDialog]);

  return (
    <BaseDateRangeDialog
      isOpen={activeDialogs[dialog.DATE_RANGE]}
      start={start}
      end={end}
      handleStartChange={handleStartChange}
      handleEndChange={handleEndChange}
      onCancel={handleCancel}
      onApply={handleApply}
      showContain={false}
    />
  );
};

export default memo(DateRange);
